import { useEffect, useMemo, useRef, useState } from 'react'
import { Banknote, ExternalLink, Plus } from 'lucide-react'
import type CrmFundraisingDonation from '@/types/serializers/CrmFundraisingDonation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

interface DonorRowLike {
  id: number
  donor_type: 'individual' | 'organization'
}

interface DonationsTabProps {
  donor: DonorRowLike
  isDonorDiscarded: boolean
}

interface PagyMeta {
  count: number
  page: number
  items: number
  pages: number
  last: number
}

interface ForDonorResponse {
  donations: CrmFundraisingDonation[]
  visible_total: number
  hidden_count: number
  pagy: PagyMeta
}

type Variant = 'default' | 'outline' | 'destructive' | 'secondary'

function statusBadgeVariant(statusKey: string): Variant {
  switch (statusKey) {
    case 'received':
      return 'default'
    case 'cancelled':
      return 'destructive'
    case 'promised':
    default:
      return 'outline'
  }
}

// The serializer's `status` attribute returns { value, label } at runtime
// but the generated DonationStatus type is a string union. Derive the
// label from status_key client-side to avoid the discrepancy.
const STATUS_LABELS: Record<string, string> = {
  promised: 'Promis',
  received: 'Reçu',
  cancelled: 'Annulé',
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}

function DonationRow({ donation }: { donation: CrmFundraisingDonation }) {
  // Cancelled rows are already signalled by the destructive Badge variant.
  // Discarded rows would also be visually de-emphasized once the serializer
  // exposes a `discarded` flag — that belongs in a follow-up (the plan's
  // SEC-PLAN-005 residual). For now both states rely on the Badge alone.
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 border-b py-2 last:border-b-0',
        donation.status_key === 'cancelled' && 'text-muted-foreground'
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground whitespace-nowrap">
            {formatDate(donation.donation_date)}
          </span>
          <span className="font-semibold whitespace-nowrap">
            {donation.amount_formatted}
          </span>
          <Badge variant={statusBadgeVariant(donation.status_key)} className="text-xs">
            {STATUS_LABELS[donation.status_key] ?? donation.status_key}
          </Badge>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate" title={donation.campaign_name || ''}>
            {donation.campaign_name || '—'}
          </span>
          <span>·</span>
          <span className="truncate">{donation.payment_method_name || '—'}</span>
        </div>
      </div>
      <a
        href={`/fundraising/donations/${donation.id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
        title="Voir le don dans un nouvel onglet"
      >
        Voir
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  )
}

export function DonationsTab({ donor, isDonorDiscarded }: DonationsTabProps) {
  const [showHidden, setShowHidden] = useState(false)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<ForDonorResponse | null>(null)
  const reloadKey = useRef(0)

  // Reset when the donor changes.
  useEffect(() => {
    setShowHidden(false)
    setPage(1)
    setData(null)
    setError(null)
  }, [donor.id, donor.donor_type])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)

    const url =
      `/fundraising/donations/for_donor/${donor.donor_type}/${donor.id}` +
      `?page=${page}&show_hidden=${showHidden ? 'true' : 'false'}`

    fetch(url, {
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token':
          (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)
            ?.content ?? '',
      },
      signal: controller.signal,
      credentials: 'same-origin',
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<ForDonorResponse>
      })
      .then((body) => setData(body))
      .catch((err) => {
        if (err.name === 'AbortError') return
        setError('Erreur de chargement des dons.')
      })
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [donor.id, donor.donor_type, showHidden, page, reloadKey.current])

  function handleRetry() {
    reloadKey.current += 1
    setError(null)
    setLoading(true)
  }

  const visibleTotal = data?.visible_total ?? 0
  const hiddenCount = data?.hidden_count ?? 0
  const total = useMemo(() => visibleTotal + hiddenCount, [visibleTotal, hiddenCount])

  const showToolbar = total > 0 || showHidden

  // CTA hrefs carry the short-form donor_type the index controllers expect.
  const newDonationHref = `/fundraising/donations?prefill_donor_type=${donor.donor_type}&prefill_donor_id=${donor.id}`
  const newRecurringHref = `/fundraising/recurring_donations?prefill_donor_type=${donor.donor_type}&prefill_donor_id=${donor.id}`

  return (
    <div className="space-y-3">
      {showToolbar && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Switch
              id="donations-show-hidden"
              checked={showHidden}
              onCheckedChange={(checked) => {
                setShowHidden(checked)
                setPage(1)
              }}
              disabled={Boolean(error)}
            />
            <Label htmlFor="donations-show-hidden" className="text-sm">
              Afficher les dons annulés et archivés
            </Label>
            <span className="text-xs text-muted-foreground">
              {loading ? (
                <Skeleton className="inline-block h-3 w-16 align-middle" />
              ) : error ? (
                '—'
              ) : hiddenCount > 0 && !showHidden ? (
                `(${hiddenCount} masqué${hiddenCount > 1 ? 's' : ''})`
              ) : null}
            </span>
          </div>
          {!isDonorDiscarded && (
            <div className="flex items-center gap-2">
              <Button asChild size="sm">
                <a href={newDonationHref}>
                  <Plus className="h-4 w-4 mr-1" />
                  Nouveau don
                  <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                </a>
              </Button>
              <Button asChild size="sm" variant="outline">
                <a href={newRecurringHref}>
                  <Plus className="h-4 w-4 mr-1" />
                  Nouveau don récurrent
                  <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                </a>
              </Button>
            </div>
          )}
        </div>
      )}

      {loading && !data && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {error && !loading && (
        <div className="flex flex-col items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-4 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={handleRetry}>
            Réessayer
          </Button>
        </div>
      )}

      {!loading && !error && data && data.donations.length > 0 && (
        <div>
          {data.donations.map((donation) => (
            <DonationRow key={donation.id} donation={donation} />
          ))}
          {data.pagy.pages > 1 && (
            <div className="flex items-center justify-between pt-3">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Précédent
              </Button>
              <span className="text-xs text-muted-foreground">
                Page {data.pagy.page} / {data.pagy.pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.pagy.pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Empty case 1: donor has zero donations of any kind */}
      {!loading && !error && data && total === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-muted-foreground">
          <Banknote className="h-10 w-10 opacity-40" />
          <p className="text-sm">Aucun don pour ce donateur</p>
          {!isDonorDiscarded && (
            <div className="flex items-center gap-2">
              <Button asChild size="sm">
                <a href={newDonationHref}>
                  <Plus className="h-4 w-4 mr-1" />
                  Nouveau don
                  <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                </a>
              </Button>
              <Button asChild size="sm" variant="outline">
                <a href={newRecurringHref}>
                  <Plus className="h-4 w-4 mr-1" />
                  Nouveau don récurrent
                  <ExternalLink className="h-3 w-3 ml-1 opacity-60" />
                </a>
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Empty case 2: donations exist but are all hidden by the switch */}
      {!loading && !error && data && total > 0 && data.donations.length === 0 && !showHidden && (
        <p className="py-6 text-center text-sm text-muted-foreground">Aucun don visible</p>
      )}
    </div>
  )
}
